import { useState, useCallback, useRef } from 'react';
import { ValidationError, validateObject } from '../utils/validation';
import { parseAmountInput } from '../utils/decimal';
import { isValidDateStamp, INVALID_DATE_MESSAGE } from '../utils/dateStamp';

/**
 * Custom hook for form validation with real-time feedback
 */
export const useFormValidation = (initialData = {}, validationSchema = {}) => {
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const validationTimeouts = useRef({});

  /**
   * Validate a single field
   */
  const validateField = useCallback((field, value) => {
    try {
      if (validationSchema[field]) {
        if (typeof validationSchema[field] === 'function') {
          validationSchema[field](value);
        } else if (Array.isArray(validationSchema[field])) {
          // Multiple validators for the same field
          for (const validator of validationSchema[field]) {
            validator(value);
          }
        }
      }
      
      // Clear error if validation passes
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
      
      return true;
    } catch (error) {
      if (error instanceof ValidationError) {
        setErrors(prev => ({
          ...prev,
          [field]: error.message
        }));
        return false;
      }
      throw error;
    }
  }, [validationSchema]);

  /**
   * Validate field with debouncing for performance
   */
  const validateFieldDebounced = useCallback((field, value, delay = 300) => {
    // Clear existing timeout
    if (validationTimeouts.current[field]) {
      clearTimeout(validationTimeouts.current[field]);
    }

    // Set new timeout
    validationTimeouts.current[field] = setTimeout(() => {
      validateField(field, value);
    }, delay);
  }, [validateField]);

  /**
   * Update field value and optionally validate
   */
  const updateField = useCallback((field, value, options = {}) => {
    const { validate = true, immediate = false } = options;
    
    setData(prev => ({ ...prev, [field]: value }));
    
    if (validate && touched[field]) {
      if (immediate) {
        validateField(field, value);
      } else {
        validateFieldDebounced(field, value);
      }
    }
  }, [touched, validateField, validateFieldDebounced]);

  /**
   * Mark field as touched and validate
   */
  const touchField = useCallback((field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, data[field]);
  }, [data, validateField]);

  /**
   * Mark multiple fields as touched
   */
  const touchFields = useCallback((fields) => {
    const newTouched = { ...touched };
    fields.forEach(field => {
      newTouched[field] = true;
    });
    setTouched(newTouched);
    
    // Validate all touched fields
    fields.forEach(field => {
      validateField(field, data[field]);
    });
  }, [touched, data, validateField]);

  /**
   * Validate all fields
   */
  const validateAll = useCallback(() => {
    setIsValidating(true);
    
    try {
      const result = validateObject(data, validationSchema);
      setErrors(result.getAllErrors());
      
      // Mark all fields as touched
      const allFields = Object.keys(validationSchema);
      const newTouched = {};
      allFields.forEach(field => {
        newTouched[field] = true;
      });
      setTouched(newTouched);
      
      return result.isValid;
    } finally {
      setIsValidating(false);
    }
  }, [data, validationSchema]);

  /**
   * Reset form to initial state
   */
  const reset = useCallback((newInitialData = initialData) => {
    setData(newInitialData);
    setErrors({});
    setTouched({});
    
    // Clear any pending validation timeouts
    Object.values(validationTimeouts.current).forEach(timeout => {
      clearTimeout(timeout);
    });
    validationTimeouts.current = {};
  }, [initialData]);

  /**
   * Set multiple field values at once
   */
  const setFields = useCallback((newData, options = {}) => {
    const { validate = false, touch = false } = options;
    
    setData(prev => ({ ...prev, ...newData }));
    
    if (touch) {
      const fieldsToTouch = Object.keys(newData);
      setTouched(prev => {
        const newTouched = { ...prev };
        fieldsToTouch.forEach(field => {
          newTouched[field] = true;
        });
        return newTouched;
      });
    }
    
    if (validate) {
      Object.entries(newData).forEach(([field, value]) => {
        if (touched[field] || touch) {
          validateField(field, value);
        }
      });
    }
  }, [touched, validateField]);

  /**
   * Get field error message
   */
  const getFieldError = useCallback((field) => {
    return errors[field] || null;
  }, [errors]);

  /**
   * Check if field has error
   */
  const hasFieldError = useCallback((field) => {
    return !!(errors[field] && touched[field]);
  }, [errors, touched]);

  /**
   * Check if field is valid
   */
  const isFieldValid = useCallback((field) => {
    return touched[field] && !errors[field];
  }, [errors, touched]);

  /**
   * Get all error messages
   */
  const getAllErrors = useCallback(() => {
    return Object.keys(errors).reduce((acc, field) => {
      if (touched[field]) {
        acc[field] = errors[field];
      }
      return acc;
    }, {});
  }, [errors, touched]);

  /**
   * Check if form is valid
   */
  const isValid = Object.keys(errors).length === 0;

  /**
   * Check if form has been modified
   */
  const isDirty = JSON.stringify(data) !== JSON.stringify(initialData);

  /**
   * Check if any field has been touched
   */
  const isTouched = Object.keys(touched).some(field => touched[field]);

  return {
    // Data
    data,
    errors,
    touched,
    
    // State flags
    isValid,
    isDirty,
    isTouched,
    isValidating,
    
    // Field operations
    updateField,
    touchField,
    touchFields,
    setFields,
    
    // Validation
    validateField,
    validateAll,
    
    // Utilities
    reset,
    getFieldError,
    hasFieldError,
    isFieldValid,
    getAllErrors
  };
};

/**
 * Hook for budget form validation
 */
export const useBudgetFormValidation = (initialData = {}) => {
  const budgetSchema = {
    name: (value) => {
      if (!value || value.trim() === '') {
        throw new ValidationError('Budget name is required');
      }
      if (value.length > 100) {
        throw new ValidationError('Budget name must be 100 characters or less');
      }
      if (/^\s+$/.test(value)) {
        throw new ValidationError('Budget name cannot be only whitespace');
      }
    },
    type: (value) => {
      const numValue = parseInt(value);
      if (isNaN(numValue) || numValue < 0 || numValue > 10) {
        throw new ValidationError('Budget type must be between 0 and 10');
      }
    }
  };

  return useFormValidation(initialData, budgetSchema);
};

/**
 * Hook for ledger form validation
 */
export const useLedgerFormValidation = (initialData = {}) => {
  const ledgerSchema = {
    name: (value) => {
      if (!value || value.trim() === '') {
        throw new ValidationError('Ledger name is required');
      }
      if (value.length > 100) {
        throw new ValidationError('Ledger name must be 100 characters or less');
      }
      if (/^\s+$/.test(value)) {
        throw new ValidationError('Ledger name cannot be only whitespace');
      }
    },
    type: (value) => {
      const validTypes = [0, 1, 2, 3, 4]; // Checking, Savings, Credit, Investment, Cash
      const numValue = parseInt(value);
      if (!validTypes.includes(numValue)) {
        throw new ValidationError('Invalid ledger type');
      }
    }
  };

  return useFormValidation(initialData, ledgerSchema);
};

// Window for the new-transaction row only. Deliberately tighter than
// DATE_STAMP_MIN / DATE_STAMP_MAX; exported so the pickers on that row can
// advertise the same bounds they are validated against (#1842).
export const TRANSACTION_DATE_MIN = '2000-01-01';

const TRANSACTION_DATE_MIN_DATE = new Date(TRANSACTION_DATE_MIN + 'T00:00:00');

/**
 * One year from today as YYYY-MM-DD, for a date input's `max`.
 */
export function transactionDateMax() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

function transactionDateMaxDate() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d;
}

/**
 * Hook for transaction form validation
 */
export const useTransactionFormValidation = (initialData = {}) => {
  const transactionSchema = {
    date: (value) => {
      if (!value) {
        throw new ValidationError('Date is required');
      }

      // Shape, then real-calendar check (#1842). The Date round-trip that used
      // to stand in for this accepts 2026-02-31, because JavaScript rolls an
      // overflowing day forward instead of reporting Invalid Date.
      if (!isValidDateStamp(value)) {
        throw new ValidationError(INVALID_DATE_MESSAGE);
      }

      // This form keeps a window tighter than the 1900-2100 the backend and the
      // other line surfaces enforce: a new transaction dated 1995 or 2050 is
      // far more likely a typo than history being entered here (#1842).
      const date = new Date(value + 'T00:00:00');
      if (date < TRANSACTION_DATE_MIN_DATE || date > transactionDateMaxDate()) {
        throw new ValidationError('Date must be between 2000 and one year from now');
      }
    },
    
    payee: (value) => {
      if (!value || value.trim() === '') {
        throw new ValidationError('Payee is required');
      }
      if (value.length > 100) {
        throw new ValidationError('Payee name must be 100 characters or less');
      }
      if (/^\s+$/.test(value)) {
        throw new ValidationError('Payee name cannot be only whitespace');
      }
    }
  };

  // Separate amount validation function
  const validateAmount = (data) => {
    const debit = parseAmountInput(data.debit);
    const credit = parseAmountInput(data.credit);
    
    if (debit < 0) {
      throw new ValidationError('Debit amount cannot be negative');
    }
    if (credit < 0) {
      throw new ValidationError('Credit amount cannot be negative');
    }

    if (debit === 0 && credit === 0) {
      throw new ValidationError('Either debit or credit amount is required');
    }

    // Validate debit amount if provided
    if (debit > 0) {
      if (debit > 999999999.99) {
        throw new ValidationError('Debit amount cannot exceed 999,999,999.99');
      }
    }

    // Validate credit amount if provided
    if (credit > 0) {
      if (credit > 999999999.99) {
        throw new ValidationError('Credit amount cannot exceed 999,999,999.99');
      }
    }
    
    // Check decimal precision for both amounts
    if (debit > 0) {
      const debitDecimalPlaces = (debit.toString().split('.')[1] || '').length;
      if (debitDecimalPlaces > 2) {
        throw new ValidationError('Debit amount cannot have more than 2 decimal places');
      }
    }
    
    if (credit > 0) {
      const creditDecimalPlaces = (credit.toString().split('.')[1] || '').length;
      if (creditDecimalPlaces > 2) {
        throw new ValidationError('Credit amount cannot have more than 2 decimal places');
      }
    }
  };

  const formValidation = useFormValidation(initialData, transactionSchema);
  
  // Override validateAll to handle custom amount validation
  const originalValidateAll = formValidation.validateAll;
  const validateAll = useCallback(() => {
    // First validate individual fields
    const isValid = originalValidateAll();
    
    // Then validate amount logic
    let amountValid = true;
    try {
      validateAmount(formValidation.data);
      // Clear amount error if validation passes
      if (formValidation.errors.amount) {
        delete formValidation.errors.amount;
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        formValidation.errors.amount = error.message;
        amountValid = false;
      }
    }
    
    return isValid && amountValid;
  }, [originalValidateAll, formValidation, validateAmount]);

  return {
    ...formValidation,
    validateAll
  };
};

export default useFormValidation;